import upperFirst from "lodash/upperFirst";
import { Fragment, useMemo, type ComponentProps, type ReactNode } from "react";

import { Typography } from "@/components/ui/typography";

import type {
  FilterDefinitionType,
  ReferenceValueOptionsType,
} from "@/components/@types";
import { FieldEnum } from "@/components/organisms/Field/FieldEnum";
import { useNodeListQuery } from "@/components/organisms/Node/Node.hooks";
import { getReferenceValueOptionsByTypename } from "@/objects";
import { State } from "@/state";
import { Utils } from "@/utils";

import type {
  ComponentFilter,
  ComponentValue,
  FilterExpr,
  FilterGroup,
} from "./types/rule";
import { getTriggerConditionFilterDefinitions } from "./utils/filterDefinitions";
import {
  isFilterGroup,
  normalizeConditionOperator,
  ruleConditionsToFilterGroup,
  type RuleConditionRow,
} from "./utils/filterGroup";

export type RuleWhenDescriptionRule = {
  triggerType?: string | null;
  triggerObjectType?: string | null;
  triggerOperation?: string | null;
  scheduleFrequency?: string | null;
  conditions?: RuleConditionRow[] | null;
};

type RuleFilterDateValue = {
  date1?: string | null;
  date2?: string | null;
};

function isRuleFilterDateValue(value: unknown): value is RuleFilterDateValue {
  return (
    typeof value === "object" &&
    value != null &&
    ("date1" in value || "date2" in value)
  );
}

function parseRuleFilterDateValue(value: unknown): RuleFilterDateValue {
  if (typeof value === "string") {
    if (!value) return {};
    try {
      const parsed = JSON.parse(value);
      if (isRuleFilterDateValue(parsed)) return parsed;
    } catch {
      return { date1: value };
    }
    return { date1: value };
  }
  if (isRuleFilterDateValue(value)) return value;
  return {};
}

function formatValueDisplay(
  value: ComponentValue,
  filterDef: FilterDefinitionType | null | undefined,
  operator: string | undefined,
): string {
  if (value.type === "input") {
    return `Template input: ${value.inputKey}`;
  }
  if (value.type === "constant") {
    const v = value.value;
    if (v == null || v === "") return "Enter value...";
    if (filterDef?.type === "date") {
      const dates = parseRuleFilterDateValue(v);
      if (operator === "range") {
        const start = dates.date1 ? Utils.fDate(new Date(dates.date1)) : null;
        const end = dates.date2 ? Utils.fDate(new Date(dates.date2)) : null;
        if (start && end) return `${start} → ${end}`;
        if (start) return `${start} → …`;
        if (end) return `… → ${end}`;
        return "Enter value...";
      }
      if (dates.date1) return Utils.fDate(new Date(dates.date1));
      return "Enter value...";
    }
    if (
      (filterDef?.type === "enum" || filterDef?.type === "enums") &&
      filterDef.getValueOptions
    ) {
      if (Array.isArray(v)) {
        return `${v.length} selected`;
      }
      return String(v);
    }
    if (filterDef?.type === "reference" && Array.isArray(v)) {
      return `${v.length} selected`;
    }
    return String(v);
  }
  if (value.path) return value.path;
  return "Select...";
}

/**
 * Renders a condition's value as the rule's description says it. Subsumes ``formatValueDisplay``
 * for the simple cases (variable references and non-reference constants)
 * and adds a special path for reference filters: when the value is a
 * live-reference identifier, look it up in the same query the picker
 * uses and render the caller's ``onRenderOption`` so the chip shows the
 * human-readable label (e.g. ``"Bill (760.00 CAD) — 49th Parallel
 * Roasters"``) instead of the raw UUID.
 *
 * Lives as its own component so the reference branch can call
 * ``useNodeListQuery`` unconditionally — the parent chip stays free of
 * conditional hooks.
 */
function RuleConditionValueLabel({
  value,
  filterDef,
  operator,
}: {
  value: ComponentValue;
  filterDef: FilterDefinitionType | null;
  operator: string;
}) {
  const activeEntityIdentifiers = State((s) => s.activeEntityIdentifiers);
  const entityIdentifier = State(
    (s) => s.scopedEntityIdentifier ?? s.activeEntityIdentifiers[0] ?? null,
  );
  const referenceOptions =
    useMemo((): ReferenceValueOptionsType<unknown> | null => {
      if (filterDef?.type !== "reference") return null;
      const entityScope =
        activeEntityIdentifiers.length > 0
          ? activeEntityIdentifiers
          : entityIdentifier
            ? [entityIdentifier]
            : undefined;
      try {
        if (filterDef.referenceTypename) {
          const fromRegistry = getReferenceValueOptionsByTypename(
            filterDef.referenceTypename,
          )(entityScope);
          const columnOpts = filterDef.getValueOptions?.(
            entityIdentifier
              ? { entity: { identifier: entityIdentifier } }
              : undefined,
          ) as ReferenceValueOptionsType<unknown> | undefined;
          const opts = {
            isNullable: columnOpts?.isNullable ?? true,
            ...fromRegistry,
            ...columnOpts,
            query: fromRegistry?.query ?? columnOpts?.query,
            queryOptions: {
              ...fromRegistry?.queryOptions,
              ...columnOpts?.queryOptions,
              variables: {
                ...fromRegistry?.queryOptions?.variables,
                ...columnOpts?.queryOptions?.variables,
                entityIdentifiers: entityScope,
              },
            },
          } as ReferenceValueOptionsType<unknown>;
          return opts?.query ? opts : null;
        }
        if (!filterDef.getValueOptions) return null;
        const probe = entityIdentifier
          ? { entity: { identifier: entityIdentifier } }
          : undefined;
        const opts = filterDef.getValueOptions(
          probe,
        ) as ReferenceValueOptionsType<unknown>;
        return opts?.query ? opts : null;
      } catch {
        return null;
      }
    }, [activeEntityIdentifiers, entityIdentifier, filterDef]);

  const isReferenceConstant =
    filterDef?.type === "reference" &&
    value.type === "constant" &&
    value.value != null &&
    value.value !== "" &&
    !(Array.isArray(value.value) && value.value.length === 0);

  if (isReferenceConstant && referenceOptions) {
    if (Array.isArray(value.value)) {
      return (
        <LiveReferenceMultiLabel
          referenceOptions={referenceOptions}
          identifiers={value.value.map(String)}
        />
      );
    }
    return (
      <LiveReferenceLabel
        referenceOptions={referenceOptions}
        identifier={String(value.value)}
      />
    );
  }

  if (
    (filterDef?.type === "enum" || filterDef?.type === "enums") &&
    value.type === "constant" &&
    value.value != null &&
    value.value !== "" &&
    !Array.isArray(value.value) &&
    filterDef.getValueOptions
  ) {
    return (
      <FieldEnum
        value={String(value.value)}
        valueOptions={filterDef.getValueOptions(undefined) as any}
        isMultiline={false}
        style={{ paddingLeft: 0, paddingRight: 0 }}
      />
    );
  }

  return <>{formatValueDisplay(value, filterDef, operator)}</>;
}

/**
 * Resolves a live-reference identifier to its rendered label by re-running
 * the same query the picker uses (``useNodeListQuery``) and dispatching
 * to the caller's ``onRenderOption``. Apollo's cache makes the second
 * call free in practice — the picker has already populated the relevant
 * connection. Falls back to the raw identifier while loading or if the
 * row has rolled out of cache.
 */
function LiveReferenceLabel({
  referenceOptions,
  identifier,
}: {
  referenceOptions: ReferenceValueOptionsType<unknown>;
  identifier: string;
}) {
  const { options } = useNodeListQuery<unknown>(referenceOptions);
  const match = options.find(
    (o) => (o as { identifier?: string }).identifier === identifier,
  );
  if (!match) return <>{identifier}</>;
  const rendered = referenceOptions.onRenderOption?.(match, 0);
  return <>{rendered ?? identifier}</>;
}

function LiveReferenceMultiLabel({
  referenceOptions,
  identifiers,
}: {
  referenceOptions: ReferenceValueOptionsType<unknown>;
  identifiers: string[];
}) {
  const { options } = useNodeListQuery<unknown>(referenceOptions);
  const labels = identifiers.map((identifier) => {
    const match = options.find(
      (o) => (o as { identifier?: string }).identifier === identifier,
    );
    if (!match) return identifier;
    const rendered = referenceOptions.onRenderOption?.(match, 0);
    if (typeof rendered === "string") return rendered;
    return identifier;
  });
  return <>{labels.join(", ")}</>;
}

export function ruleTriggerEventClause(
  rule: RuleWhenDescriptionRule | null | undefined,
  job?: string | null,
): string | null {
  if (rule?.triggerType != null || rule?.triggerObjectType != null) {
    const triggerType = (rule.triggerType ?? "manual").toLowerCase();
    if (triggerType === "event") {
      const objectType = rule.triggerObjectType ?? "TransactionObject";
      const operation = rule.triggerOperation ?? "ON_CREATE";
      if (objectType === "TransactionObject" && operation === "ON_CREATE") {
        return "a bank transaction is created";
      }
      const objectLabel = objectType
        .replace(/Object$/, "")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .toLowerCase();
      const operationLabel = operation
        .replace(/^ON_/, "")
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/create$/, "created")
        .replace(/update$/, "updated")
        .replace(/delete$/, "deleted");
      return `a ${objectLabel} is ${operationLabel}`;
    }
    if (triggerType === "schedule") {
      const frequency = rule.scheduleFrequency?.toLowerCase() ?? "scheduled";
      return `on a ${frequency} schedule`;
    }
    if (triggerType === "manual") {
      return "runs manually";
    }
  }

  if (job === "MATCH_DOCUMENT") {
    return "a bank transaction is created";
  }

  return null;
}

function fieldShortName(
  field: string,
  filterDef: FilterDefinitionType | null | undefined,
): string {
  return (filterDef?.name ?? field.replace(/_/g, " ")).toLowerCase();
}

function formatNullCheckProse(
  leaf: ComponentFilter,
  filterDef: FilterDefinitionType | null | undefined,
): string | null {
  const normalized = normalizeConditionOperator(leaf.operator);
  if (normalized !== "isNull" && normalized !== "isNotNull") return null;
  const name = fieldShortName(leaf.field, filterDef);
  const wantsAbsent =
    (normalized === "isNull" && !leaf.not) ||
    (normalized === "isNotNull" && leaf.not);
  return wantsAbsent ? `there is no ${name}` : `there is a ${name}`;
}

function fieldProseLabel(
  field: string,
  filterDef: FilterDefinitionType | null | undefined,
): string {
  const name = filterDef?.name ?? field.replace(/_/g, " ");
  return `the ${name.toLowerCase()}`;
}

function operatorProse(
  operator: string,
  filterDef: FilterDefinitionType | null | undefined,
  negated?: boolean,
): string {
  const normalized = normalizeConditionOperator(operator);
  const flatOps = filterDef?.operators?.flat() ?? [];
  const op = flatOps.find((candidate) => candidate.key === normalized);
  // As the where editor writes them, where the field's own filters don't
  // name them.
  const said: Record<string, string> = {
    eq: "is",
    ne: "is not",
    startsWith: "starts with",
    endsWith: "ends with",
  };
  let phrase = op?.name?.toLowerCase() ?? said[normalized] ?? normalized;
  if (phrase === "is exactly") phrase = "is";
  if (phrase === "is not null") phrase = "is set";
  if (phrase === "is null") phrase = "is not set";
  if (negated) {
    if (phrase === "contains") return "does not contain";
    if (phrase === "is") return "is not";
    return `not ${phrase}`;
  }
  return phrase;
}

function isValueLessOperator(operator: string): boolean {
  const normalized = normalizeConditionOperator(operator);
  return normalized === "isNull" || normalized === "isNotNull";
}

function formatConstantValueSync(
  value: unknown,
  filterDef: FilterDefinitionType | null | undefined,
): string {
  if (value == null || value === "") return "";
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry)).join(", ");
  }
  if (filterDef?.type === "text") {
    return `"${String(value)}"`;
  }
  return String(value);
}

function formatLeafClauseSync(
  leaf: ComponentFilter,
  filterDefs: Record<string, FilterDefinitionType>,
  capitalizeFirst = false,
): string {
  const filterDef = filterDefs[leaf.field] ?? null;
  const nullProse = formatNullCheckProse(leaf, filterDef);
  if (nullProse) {
    return capitalizeFirst ? upperFirst(nullProse) : nullProse;
  }
  const operator = operatorProse(leaf.operator, filterDef, leaf.not);
  const fieldLabel = fieldProseLabel(leaf.field, filterDef);
  let clause: string;
  if (isValueLessOperator(leaf.operator)) {
    clause = `${fieldLabel} ${operator}`;
  } else if (leaf.value.type !== "constant") {
    clause = `${fieldLabel} ${operator} …`;
  } else {
    const valueText = formatConstantValueSync(leaf.value.value, filterDef);
    clause = valueText
      ? `${fieldLabel} ${operator} ${valueText}`
      : `${fieldLabel} ${operator}`;
  }
  return capitalizeFirst ? upperFirst(clause) : clause;
}

function formatLeafClause(
  leaf: ComponentFilter,
  filterDefs: Record<string, FilterDefinitionType>,
  capitalizeFirst = false,
): ReactNode {
  const filterDef = filterDefs[leaf.field] ?? null;
  const nullProse = formatNullCheckProse(leaf, filterDef);
  if (nullProse) {
    return <>{capitalizeFirst ? upperFirst(nullProse) : nullProse}</>;
  }
  const operator = operatorProse(leaf.operator, filterDef, leaf.not);
  const fieldLabel = capitalizeFirst
    ? upperFirst(fieldProseLabel(leaf.field, filterDef))
    : fieldProseLabel(leaf.field, filterDef);
  if (isValueLessOperator(leaf.operator)) {
    return (
      <>
        {fieldLabel} {operator}
      </>
    );
  }
  if (
    filterDef?.type === "text" &&
    leaf.value.type === "constant" &&
    typeof leaf.value.value === "string" &&
    leaf.value.value !== ""
  ) {
    return (
      <>
        {fieldLabel} {operator} "{leaf.value.value}"
      </>
    );
  }
  return (
    <>
      {fieldLabel} {operator}{" "}
      <RuleConditionValueLabel
        value={leaf.value}
        filterDef={filterDef}
        operator={leaf.operator}
      />
    </>
  );
}

function formatFilterGroupProse(
  group: FilterGroup,
  filterDefs: Record<string, FilterDefinitionType>,
  capitalizeState?: { pending: boolean },
): ReactNode | null {
  const state = capitalizeState ?? { pending: false };
  if (group.children.length === 0) return null;
  const joiner = group.type === "or" ? " or " : " and ";
  const parts = group.children
    .map((child, index) => {
      const prose = isFilterGroup(child)
        ? formatFilterGroupProse(child, filterDefs, state)
        : formatLeafClause(child, filterDefs, state.pending);
      if (!isFilterGroup(child) && state.pending) {
        state.pending = false;
      }
      if (!prose) return null;
      return <Fragment key={index}>{prose}</Fragment>;
    })
    .filter(Boolean) as ReactNode[];
  if (parts.length === 0) return null;
  return parts.reduce<ReactNode>(
    (acc, part, index) =>
      index === 0 ? (
        part
      ) : (
        <>
          {acc}
          {joiner}
          {part}
        </>
      ),
    null,
  );
}

function formatFilterGroupProseSync(
  group: FilterGroup,
  filterDefs: Record<string, FilterDefinitionType>,
  capitalizeState?: { pending: boolean },
): string | null {
  const state = capitalizeState ?? { pending: false };
  if (group.children.length === 0) return null;
  const joiner = group.type === "or" ? " or " : " and ";
  const parts = group.children
    .map((child) => {
      const part = isFilterGroup(child)
        ? formatFilterGroupProseSync(child, filterDefs, state)
        : formatLeafClauseSync(child, filterDefs, state.pending);
      if (!isFilterGroup(child) && state.pending) {
        state.pending = false;
      }
      return part;
    })
    .filter((part): part is string => !!part);
  if (parts.length === 0) return null;
  return parts.join(joiner);
}

function isDefaultBankTransactionTrigger(
  rule: RuleWhenDescriptionRule | null | undefined,
  job?: string | null,
): boolean {
  if (job === "MATCH_DOCUMENT") {
    return true;
  }
  const triggerType = (rule?.triggerType ?? "event").toLowerCase();
  if (triggerType !== "event") {
    return false;
  }
  const objectType = rule?.triggerObjectType ?? "TransactionObject";
  const operation = rule?.triggerOperation ?? "ON_CREATE";
  return objectType === "TransactionObject" && operation === "ON_CREATE";
}

export function ruleWhenDescriptionBody(
  rule: RuleWhenDescriptionRule | null | undefined,
  job?: string | null,
  filterGroup?: FilterGroup,
  capitalize = false,
): string | null {
  const filterDefs = getTriggerConditionFilterDefinitions();
  const conditionsGroup =
    filterGroup ?? ruleConditionsToFilterGroup(rule?.conditions ?? []);
  const conditionsProse = formatFilterGroupProseSync(
    conditionsGroup,
    filterDefs,
    capitalize ? { pending: true } : undefined,
  );
  if (conditionsProse) {
    return conditionsProse;
  }
  const triggerClause = ruleTriggerEventClause(rule, job);
  if (!triggerClause || isDefaultBankTransactionTrigger(rule, job)) {
    return null;
  }
  return capitalize ? upperFirst(triggerClause) : triggerClause;
}

function endsWithSentencePunctuation(text: string): boolean {
  return /[.!?]$/.test(text.trim());
}

function formatWhenSentenceBody(body: string, capitalize: boolean): string {
  let text = body.trim();
  if (!text) {
    return text;
  }
  if (capitalize) {
    text = upperFirst(text);
  }
  if (!endsWithSentencePunctuation(text)) {
    text = `${text}.`;
  }
  return text;
}

function WhenSentencePeriod({ children }: { children: ReactNode }) {
  return <>{children}.</>;
}

export function ruleWhenDescriptionSearchText(
  rule: RuleWhenDescriptionRule | null | undefined,
  job?: string | null,
  filterGroup?: FilterGroup,
): string | null {
  const body = ruleWhenDescriptionBody(rule, job, filterGroup);
  return body ? `When ${formatWhenSentenceBody(body, false)}` : null;
}

export function RuleWhenDescription({
  rule,
  job,
  filterGroup,
  omitPrefix = false,
  ...typographyProps
}: {
  rule: RuleWhenDescriptionRule | null | undefined;
  job?: string | null;
  filterGroup?: FilterGroup;
  omitPrefix?: boolean;
} & ComponentProps<typeof Typography>) {
  const filterDefs = useMemo(() => getTriggerConditionFilterDefinitions(), []);
  const conditionsGroup = useMemo(
    () => filterGroup ?? ruleConditionsToFilterGroup(rule?.conditions ?? []),
    [filterGroup, rule?.conditions],
  );
  const capitalizeState = omitPrefix ? { pending: true } : undefined;
  const conditionsProse = formatFilterGroupProse(
    conditionsGroup,
    filterDefs,
    capitalizeState,
  );
  const triggerClause = ruleTriggerEventClause(rule, job);
  const triggerBody =
    triggerClause && !isDefaultBankTransactionTrigger(rule, job)
      ? capitalizeState?.pending
        ? upperFirst(triggerClause)
        : triggerClause
      : null;
  const body = conditionsProse ?? triggerBody;

  if (!body) {
    return null;
  }

  if (typeof body === "string") {
    const sentence = formatWhenSentenceBody(body, omitPrefix);
    return (
      <Typography {...typographyProps}>
        {omitPrefix ? sentence : `When ${formatWhenSentenceBody(body, false)}`}
      </Typography>
    );
  }

  return (
    <Typography {...typographyProps}>
      {omitPrefix ? (
        <WhenSentencePeriod>{body}</WhenSentencePeriod>
      ) : (
        <>
          When <WhenSentencePeriod>{body}</WhenSentencePeriod>
        </>
      )}
    </Typography>
  );
}
